import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { clsx } from 'clsx';
import { useDocumentStore } from '../../stores/documentStore';
import InlineMathField, { InlineMathModal } from '../math/InlineMathField';

const RESPONSE_TYPES = [
  { value: 'multiple-choice', label: 'Multiple Choice' },
  { value: 'multi-select', label: 'Multi Select' },
  { value: 'short-answer', label: 'Short Answer' },
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'image-upload', label: 'Image Upload' },
];

const ResponseAreaConfigModal = ({ isOpen, blockId, config, onClose, onSaved, onDuplicate }) => {
  const { updateResponseArea } = useDocumentStore();

  const [type, setType] = useState('multiple-choice');
  const [label, setLabel] = useState('');
  const [required, setRequired] = useState(true);
  const [points, setPoints] = useState(1);
  const [options, setOptions] = useState(['', '']);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [showOptionMath, setShowOptionMath] = useState(false);
  const [optionMathIndex, setOptionMathIndex] = useState(null);
  const [optionMathCursor, setOptionMathCursor] = useState({ start: 0, end: 0 });
  const [optionMathLatex, setOptionMathLatex] = useState('');
  const [optionMathAnchor, setOptionMathAnchor] = useState(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const modalRef = useRef(null);
  const optionInputRefs = useRef({});

  useEffect(() => {
    if (!isOpen || !config) return;
    setType(config.type || 'multiple-choice');
    setLabel(config.label || '');
    setRequired(config.required !== false);
    setPoints(config.points || 1);
    setOptions(config.options || ['', '']);
    setCorrectAnswer(config.correctAnswer || null);
    const initialCorrectAnswers = Array.isArray(config.correctAnswers)
      ? config.correctAnswers
      : (config.correctAnswer ? [config.correctAnswer] : []);
    if (
      (config.type === 'short-answer' || config.type === 'paragraph') &&
      initialCorrectAnswers.length === 0
    ) {
      initialCorrectAnswers.push('');
    }
    setCorrectAnswers(initialCorrectAnswers);
  }, [isOpen, config]);

  useEffect(() => {
    if (!isOpen) return;
    setPosition({ x: 0, y: 0 });
    setIsDragging(false);
  }, [isOpen]);

  useEffect(() => {
    if (type === 'multiple-choice') {
      setCorrectAnswers([]);
      return;
    }
    setCorrectAnswer(null);
    if ((type === 'short-answer' || type === 'paragraph') && (!correctAnswers || correctAnswers.length === 0)) {
      setCorrectAnswers(['']);
    }
  }, [type]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMove = (event) => {
      setPosition({
        x: event.clientX - dragOffsetRef.current.x,
        y: event.clientY - dragOffsetRef.current.y,
      });
    };
    const handleUp = () => setIsDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [isDragging]);

  if (!isOpen || !blockId) return null;

  const hasOptions = type === 'multiple-choice' || type === 'multi-select';

  const handleSave = () => {
    const trimmedOptions = hasOptions ? options.filter((o) => o.trim()) : null;
    const trimmedCorrectAnswers = Array.isArray(correctAnswers)
      ? correctAnswers.map((a) => a.trim()).filter((a) => a)
      : [];
    updateResponseArea(blockId, {
      type,
      label,
      required,
      points,
      options: trimmedOptions,
      correctAnswer: type === 'multiple-choice' ? correctAnswer : null,
      correctAnswers: (
        type === 'multi-select' ||
        type === 'short-answer' ||
        type === 'paragraph'
      ) ? trimmedCorrectAnswers : null,
    });
    onSaved?.();
    onClose();
  };

  const handleAddOption = () => setOptions([...options, '']);
  const handleRemoveOption = (idx) => setOptions(options.filter((_, i) => i !== idx));
  const handleOptionChange = (idx, val) => {
    const next = [...options];
    next[idx] = val;
    setOptions(next);
  };
  const handleCorrectAnswerChange = (idx, val) => {
    const next = [...(correctAnswers || [])];
    next[idx] = val;
    setCorrectAnswers(next);
  };
  const handleAddCorrectAnswer = () => {
    setCorrectAnswers([...(correctAnswers || []), '']);
  };
  const handleRemoveCorrectAnswer = (idx) => {
    setCorrectAnswers((correctAnswers || []).filter((_, i) => i !== idx));
  };
  const openOptionMathEditor = (idx) => {
    const input = optionInputRefs.current[idx];
    if (!input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const selected = input.value.substring(start, end);
    setOptionMathCursor({ start, end });
    setOptionMathIndex(idx);
    if (selected && selected.startsWith('$') && selected.endsWith('$')) {
      setOptionMathLatex(selected.slice(1, -1));
    } else {
      setOptionMathLatex(selected || '');
    }
    setOptionMathAnchor(input.getBoundingClientRect());
    setShowOptionMath(true);
  };

  const closeOptionMathEditor = () => {
    setShowOptionMath(false);
    setOptionMathIndex(null);
    setOptionMathLatex('');
    setOptionMathAnchor(null);
  };

  const handleInsertOptionMath = (latex) => {
    if (optionMathIndex == null || !latex) {
      closeOptionMathEditor();
      return;
    }

    const current = options[optionMathIndex] || '';
    const { start, end } = optionMathCursor;
    const nextValue =
      current.substring(0, start) +
      `$${latex}$` +
      current.substring(end);

    handleOptionChange(optionMathIndex, nextValue);

    setTimeout(() => {
      const input = optionInputRefs.current[optionMathIndex];
      if (!input) return;
      const newPos = start + latex.length + 2;
      input.focus();
      input.setSelectionRange(newPos, newPos);
    }, 0);

    closeOptionMathEditor();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div
        ref={modalRef}
        className="w-full max-w-lg bg-white rounded-xl shadow-2xl border border-gray-200"
        style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
      >
        <div
          className="flex items-center justify-between px-5 py-4 border-b border-gray-200 cursor-move select-none"
          onMouseDown={(event) => {
            if (event.button !== 0) return;
            const rect = modalRef.current?.getBoundingClientRect();
            if (rect) {
              dragOffsetRef.current = { x: event.clientX - rect.left, y: event.clientY - rect.top };
              setIsDragging(true);
            }
          }}
        >
          <h3 className="text-lg font-semibold text-gray-900">Configure Response Area</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center text-gray-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
            >
              {RESPONSE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Label */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Q1, Question 1a"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
            />
          </div>

          {/* Points */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
              <input
                type="number"
                min="0"
                value={points}
                onChange={(e) => setPoints(Number(e.target.value))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
              />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input
                id="ra-required"
                type="checkbox"
                checked={required}
                onChange={(e) => setRequired(e.target.checked)}
                className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500"
              />
              <label htmlFor="ra-required" className="text-sm text-gray-700">Required</label>
            </div>
          </div>

          {/* Options for MC / multi-select */}
          {hasOptions && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
              <div className="space-y-2">
                {options.map((opt, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-5 text-right">{idx + 1}.</span>
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => handleOptionChange(idx, e.target.value)}
                      onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'm') {
                          e.preventDefault();
                          openOptionMathEditor(idx);
                        }
                      }}
                      ref={(el) => {
                        if (el) optionInputRefs.current[idx] = el;
                      }}
                      placeholder={`Option ${idx + 1}`}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                    />
                    <button
                      type="button"
                      onClick={() => openOptionMathEditor(idx)}
                      className="text-xs text-sigma-700 hover:text-sigma-900"
                      title="Insert math (Ctrl+M)"
                    >
                      Insert Math
                    </button>
                    {options.length > 2 && (
                      <button
                        onClick={() => handleRemoveOption(idx)}
                        className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={handleAddOption}
                className="mt-2 inline-flex items-center gap-1 text-sm text-sigma-700 hover:text-sigma-900"
              >
                <Plus className="w-3.5 h-3.5" /> Add option
              </button>
            </div>
          )}

          {/* Model solution (optional, for auto-grading) */}
          {type === 'multiple-choice' && options.some((o) => o.trim()) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model Solution <span className="text-gray-400">(correct option)</span>
              </label>
              <select
                value={correctAnswer || ''}
                onChange={(e) => setCorrectAnswer(e.target.value || null)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
              >
                <option value="">None selected</option>
                {options.filter((o) => o.trim()).map((opt, idx) => (
                  <option key={idx} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          )}

          {type === 'multi-select' && options.some((o) => o.trim()) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model Solution <span className="text-gray-400">(select all correct)</span>
              </label>
              <div className="space-y-2">
                {options.filter((o) => o.trim()).map((opt, idx) => {
                  const checked = correctAnswers.includes(opt);
                  return (
                    <label key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setCorrectAnswers((prev) => [...prev, opt]);
                          } else {
                            setCorrectAnswers((prev) => prev.filter((val) => val !== opt));
                          }
                        }}
                        className="rounded border-gray-300 text-sigma-600 focus:ring-sigma-500"
                      />
                      <span>{opt}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {(type === 'short-answer' || type === 'paragraph') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Model Solution <span className="text-gray-400">(one per line)</span>
              </label>
              <div className="space-y-2">
                {(correctAnswers || []).map((answer, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <InlineMathField
                      value={answer}
                      onChange={(val) => handleCorrectAnswerChange(idx, val)}
                      placeholder="e.g. x = 4"
                      rows={2}
                      textareaClassName="text-sm rounded-lg focus:ring-sigma-500 focus:border-sigma-500"
                      buttonLabel="Insert Math (Ctrl+M)"
                      buttonClassName="text-xs text-sigma-700 hover:text-sigma-900"
                      buttonTitle="Insert mathematical expression (Ctrl+M)"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveCorrectAnswer(idx)}
                      className="mt-1 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                      title="Remove answer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={handleAddCorrectAnswer}
                className="mt-2 inline-flex items-center gap-1 text-sm text-sigma-700 hover:text-sigma-900"
              >
                <Plus className="w-3.5 h-3.5" /> Add answer
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200">
          {onDuplicate && (
            <button
              type="button"
              onClick={onDuplicate}
              className="mr-auto px-4 py-2 text-sm font-medium rounded-lg bg-sigma-100 text-sigma-800 hover:bg-sigma-200"
            >
              Duplicate
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={clsx(
              'px-4 py-2 text-sm font-medium rounded-lg text-white',
              'bg-sigma-700 hover:bg-sigma-600'
            )}
          >
            Save
          </button>
        </div>
      </div>

      <InlineMathModal
        isOpen={showOptionMath}
        initialLatex={optionMathLatex}
        anchorRect={optionMathAnchor}
        onInsert={handleInsertOptionMath}
        onClose={closeOptionMathEditor}
      />
    </div>
  );
};

export default ResponseAreaConfigModal;
